/* eslint-disable consistent-return */
import { NAMESPACE } from '@ezugi/constants/media';

interface StorageError extends Error {
  notFound?: boolean;
  key?: string;
}

type StorageValue = Record<string, string>;

const getInvalidPutParamsError = (key: string): string => `Invalid parameters to put, (${key}, undefined)`;

const getNotFoundError = (k: string): string => `Not Found [${k}]`;

const _notFound = (k: string): StorageError => {
  const err = new Error(getNotFoundError(k)) as StorageError;
  err.notFound = true;
  err.key = k;
  return err;
};

/**
 * We need to check if sessionStorage access is supported
 */
const canUseSessionStorage = () => {
  const key = '__TEST_SESSION_STORAGE__';
  try {
    window.sessionStorage.setItem(key, '1');
    window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
    return true;
  } catch (_) {
    return false;
  }
};

export class SessionStorage {

  private _namespace: string;

  private _store: Storage | StorageValue;

  private _sep: string;

  constructor(namespace: string) {
    this._namespace = namespace;
    this._store = canUseSessionStorage() ? window.sessionStorage : {};
    this._sep = '___';
  }

  clear(): void {
    try {
      if ('clear' in this._store && typeof this._store.clear === 'function') {
        this._store.clear();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  }

  get(key: string): [StorageError] | [null, any] {
    const k = [ this._namespace, key ].join(this._sep);
    if (!(this._store as StorageValue)[k]) return [ _notFound(k) ];

    try {
      return [ null, JSON.parse((this._store as StorageValue)[k]) ];
    } catch (err) {
      return [ err as Error ];
    }
  }

  put(key: string, value: any): [Error] | [null, string] {
    if (typeof value === 'undefined') {
      return [ new Error(getInvalidPutParamsError(key)) ];
    }

    try {
      const k = [ this._namespace, key ].join(this._sep);
      const v = JSON.stringify(value);
      const result = ((this._store as StorageValue)[k] = v);
      return [ null, result ];
    } catch (err) {
      return [ err as Error ];
    }
  }

  has(key: string): [StorageError] | [null, boolean] {
    const k = [ this._namespace, key ].join(this._sep);
    if (!(this._store as StorageValue)[k]) return [ _notFound(k) ];
    return [ null, true ];
  }

  del(key?: string): [StorageError] | [null] | undefined {
    if (key) {
      const k = [ this._namespace, key ].join(this._sep);
      if (!(this._store as StorageValue)[k]) return [ _notFound(k) ];

      delete (this._store as StorageValue)[k];
      return [ null ];
    }

    Object.keys(this._store as StorageValue).forEach((k) => {
      const ns = k.split(this._sep)[0];
      if (ns === this._namespace) {
        delete (this._store as StorageValue)[k];
      }
    });
  }

  search(pattern: RegExp): [null, Array<{ key: string; value: string }>] {
    if (!pattern) {
      throw new Error('A pattern is required');
    }

    const matchKeys = (key: string): string | undefined => {
      const [ , _key ] = key.split(this._sep);

      if (!_key) return;
      if (!pattern.test(_key)) return;

      return key;
    };

    const makePairs = (key: string) => ({
      key: key.split(this._sep)[1]?.replace('_', '') || '',
      value: (this._store as StorageValue)[key],
    });

    return [ null, Object.keys(this._store as StorageValue).filter(matchKeys).map(makePairs) ];
  }

}

export default new SessionStorage(NAMESPACE);
